import type {PageAccessDeniedCode, PageAccessDeniedResponse} from '../dto/pageAccess';

/**
 * Les codes de refus d'accès à une page entreprise, côté clients (web, extension, app).
 *
 * ⚠️ MIROIR de l'union `PageAccessDeniedCode` de `common/src/services/pageAccess.service.ts`
 * (le serveur) : les deux listes doivent rester identiques. Le tableau existe parce qu'un
 * type ne survit pas à la compilation et qu'un client doit pouvoir reconnaître, à l'exécution,
 * un corps de 403 qui parle d'une page.
 */
export const PAGE_ACCESS_DENIED_CODES = [
  'page_not_member',
  'page_ambiguous',
  'page_rights_revoked',
  'page_cannot_create_posts',
  'page_premium_required',
  'page_owner_premium_required',
  'page_not_shared',
  'page_share_expired',
  'team_draft_not_allowed',
  'team_publish_not_allowed',
] as const satisfies readonly PageAccessDeniedCode[];

export function isPageAccessDeniedCode(value: unknown): value is PageAccessDeniedCode {
  return typeof value === 'string' && (PAGE_ACCESS_DENIED_CODES as readonly string[]).includes(value);
}

/** Un corps de réponse `403` qui décrit un refus d'accès à une page (cf. `PageAccessDeniedResponse`). */
export function isPageAccessDeniedResponse(value: unknown): value is PageAccessDeniedResponse {
  if (typeof value !== 'object' || value === null) {
    return false;
  }
  const candidate = value as {code?: unknown; message?: unknown};
  return isPageAccessDeniedCode(candidate.code) && typeof candidate.message === 'string';
}

/**
 * L'action que l'app doit proposer avec le message : c'est la moitié utile d'un refus.
 * - `upgrade` : c'est MON plan qui manque → bouton vers l'abonnement ;
 * - `reconnect` : ce sont MES droits LinkedIn → bouton vers la reconnexion ;
 * - `teamSettings` : c'est un réglage de l'équipe (partage de page, « publier en mon nom ») ;
 * - `notifyOwner` : c'est le porteur qui doit agir, rien à cliquer pour moi ;
 * - `pickPage` : préciser la page (identifiant ambigu, MCP surtout).
 */
export type PageAccessAction = 'upgrade' | 'reconnect' | 'teamSettings' | 'notifyOwner' | 'pickPage';

export function pageAccessActionFor(code: PageAccessDeniedCode): PageAccessAction {
  switch (code) {
    case 'page_premium_required':
      return 'upgrade';
    case 'page_rights_revoked':
    case 'page_cannot_create_posts':
    case 'page_not_member':
      return 'reconnect';
    case 'page_not_shared':
    case 'team_draft_not_allowed':
    case 'team_publish_not_allowed':
      return 'teamSettings';
    case 'page_owner_premium_required':
    case 'page_share_expired':
      return 'notifyOwner';
    case 'page_ambiguous':
      return 'pickPage';
  }
}
